import React, { useContext, useState, useMemo } from "react";
import { Formik, Form } from "formik";
import { useTheme } from "styled-components";
import UserContext from "../../context/user/UserContext";
import Field from "../UI/forms/Field";
import FormButtonCluster from "../UI/forms/ButtonCluster";
import { PageWrapper } from "../UI/Grid";
import * as yup from "yup";
import * as productManagementRequests from "../../requests/productManagementRequests";
import { useNavigate, useParams } from "react-router-dom";
import { Checkbox, Radio } from "../UI/forms";
import { Flex, Box } from "reflexbox";
import { useQuery } from "../Hooks";
import HeadingSection from "../UI/HeadingSection";
import Button from "../UI/Button";
import Loading from "../UI/Loading";
import { useCallback } from "react";
import { getPlatforms } from "../../requests/platformManagementRequests";

const validationSchema = yup.object().shape({
  pid: yup.string(),
  Name: yup.string(),
  Description: yup.string().max(250),
  ProductNumber: yup.string(),
  ApplicationInstance: yup.string(),
  ApplicationName: yup.string(),
  HeaderId: yup.string(),
  Profile: yup.string(),
  // img: yup.string(),
  Ruleset: yup.string(),
  Namespace: yup.string(),
  Server: yup.string(),
  isNonConfigurable: yup.boolean().default(false),
  defaultPrice: yup.number(),
  isQuickConfig: yup.boolean().default(true),
  quantityInStock: yup.number(),
  RelatedItem: yup.string(),
});

const ProductForm = () => {
  const theme = useTheme();

  const { action, id } = useParams();
  const navigate = useNavigate();
  const { activeUser, pid } = useContext(UserContext);
  const { roleLevel } = activeUser || {};

  const {
    getProduct,
    getProducts,
    updateProduct,
    deleteProduct,
    createProduct,
  } = productManagementRequests;

  const [canRefetch, setCanRefetch] = useState(false);

  const { refetchQuery } = useQuery({
    queryID: "productspage",
    request: getProducts,
    queryVariables: { pid, gid: activeUser?.groupId },
    skip: !canRefetch || !pid,
  });

  const {
    data,
    isLoading,
    refetchQuery: refetchProduct,
  } = useQuery({
    queryID: id,
    request: getProduct,
    queryVariables: { id },
    forcedFilters: {},
    skip: action === "create",
  });

  const initialValues = useMemo(
    () => ({
      pid: data?.pid || "",
      Name: data?.Name || "",
      Description: data?.Description || "",
      ProductNumber: data?.ProductNumber || "",
      ApplicationInstance: data?.ApplicationInstance || "",
      ApplicationName: data?.ApplicationName || "",
      HeaderId: data?.HeaderId || "",
      Profile: data?.Profile || "",
      img: data?.img || "",
      Ruleset: data?.Ruleset || "",
      Namespace: data?.Namespace || "",
      Server: data?.Server || "",
      isNonConfigurable: data?.isNonConfigurable || false,
      defaultPrice: data?.defaultPrice || 0,
      isQuickConfig: data?.isQuickConfig || false,
      quantityInStock: data?.quantityInStock || 0,
      RelatedItem: data?.RelatedItem || "",
      ImageUrl: data?.ImageUrl || "",
    }),
    [data]
  );

  const platforms = useQuery({
    queryID: "platforms",
    request: getPlatforms,
    skip: isLoading,
  });

  const { rows: _platforms = [] } = platforms?.data || {};
  const platformOptions = useMemo(() => {
    return _platforms.map(({ id, name }) => (
      <option key={id} value={id}>
        {name}
      </option>
    ));
  }, [_platforms]);

  const onSubmit = useCallback(
    async (values) => {
      const { username } = activeUser;
      setCanRefetch(true);
      const input = {
        ...values,
        createdByEmail: username,
        pid: activeUser?.roleLevel === 9 ? values?.pid : pid,
        // img: values.img || data?.img || "",
        img: undefined,
        ...(values.isNonConfigurable
          ? {
              isQuickConfig: false,
            }
          : {
              quantityInStock: null,
              defaultPrice: null,
            }),
      };
      if (action === "create") {
        const data = await createProduct({ input });
        refetchQuery("productspage");
        if (data.success) {
          navigate(`/Products/edit/${data?.error?.createdProduct?.ID}`);
        }
      } else if (action === "edit") {
        await updateProduct({ id, input });
        refetchProduct();
        refetchQuery("productspage");
      }
      return;
    },
    [
      action,
      activeUser,
      createProduct,
      id,
      navigate,
      pid,
      refetchProduct,
      refetchQuery,
      updateProduct,
    ]
  );

  const onDelete = useCallback(async () => {
    setCanRefetch(true);
    if (data.ID) {
      let data = await deleteProduct(id);
      refetchQuery("productspage");
      if (data.success) {
        navigate("/Products");
      }
    }
  }, [deleteProduct, id, data, navigate, refetchQuery]);

  const onTextareaInput = (e) => {
    e.target.value = e.target.value.replace(/\r?\n/, ""); // Filter out newlines
  };

  // const onFileUpload = useCallback(
  //   async (file) => {
  //     setCanRefetch(true);
  //     // const { name } = file;

  //     const formData = new FormData();
  //     formData.append("file", file);
  //     const preview = URL.createObjectURL(file);
  //     refetchProduct();
  //     refetchQuery("productspage");
  //   },
  //   [refetchProduct, refetchQuery]
  // );

  return (
    <PageWrapper>
      <HeadingSection
        my="1rem"
        hideCreate
        title={
          action === "create"
            ? "Create Product"
            : `Editing Product: ${data?.Name || ""}`
        }
      />

      {isLoading ? (
        <Loading />
      ) : (
        <Formik
          validationSchema={validationSchema}
          initialValues={initialValues}
          onSubmit={onSubmit}
          validateOnBlur={false}
          enableReinitialize
        >
          {(formProps) => {
            const { values, setFieldValue } = formProps;

            return (
              <Form>
                <Flex width={1} justifyContent="center">
                  <Box>
                    <Radio
                      id="isNonConfigurable"
                      name="isNonConfigurable"
                      options={[
                        {
                          value: false,
                          label: (
                            <Button
                              onClick={() =>
                                setFieldValue("isNonConfigurable", false)
                              }
                              color={
                                values.isNonConfigurable
                                  ? theme.gray
                                  : theme.secondary
                              }
                            >
                              Configurable
                            </Button>
                          ),
                        },
                        {
                          value: true,
                          label: (
                            <Button
                              onClick={() =>
                                setFieldValue("isNonConfigurable", true)
                              }
                              color={
                                !values.isNonConfigurable
                                  ? theme.gray
                                  : theme.secondary
                              }
                            >
                              Fixed Price
                            </Button>
                          ),
                        },
                      ]}
                    />
                  </Box>
                </Flex>

                <Flex
                  alignItems="flex-start"
                  width={1}
                  style={{ gap: "1rem" }}
                  flexDirection={["column", "column", "row"]}
                >
                  <Flex
                    flexDirection="column"
                    alignItems="flex-start"
                    width={[1, 1, 0.5]}
                  >
                    {activeUser?.roleLevel === 9 && (
                      <Field
                        required
                        labelText="Assign to Platform"
                        name="pid"
                        as="select"
                      >
                        <option key="default" value="">
                          Select a Platform
                        </option>
                        {platformOptions}
                      </Field>
                    )}
                    <Field
                      disabled={roleLevel < 9}
                      labelText="Name: "
                      name="Name"
                    />
                    <Field
                      disabled={roleLevel < 9}
                      labelText="Part Number: "
                      name="ProductNumber"
                    />
                    <Field
                      disabled={roleLevel < 9}
                      labelText="Description: "
                      name="Description"
                      component="textarea"
                      style={{ resize: "none" }}
                      onInput={onTextareaInput}
                    />
                     <Field
                      disabled={roleLevel < 9}
                      labelText="Image URL: "
                      name="ImageUrl"
                    />
                    {/* <Flex width={1} flexDirection="column" my="0.5rem">
                      <Label htmlFor="img">Product Image:</Label>
                      <Flex mt="-1rem" width={1} alignItems="flex-start">
                        {isLoading ? (
                          <Card mt="1rem" width="348px" height="348px">
                            <Loading relative />
                          </Card>
                        ) : (
                          <Card mt="1rem" mr="1rem" width="348px" height="100%">
                            {values?.img && (
                              <img src={values.img} alt={values?.Name || ""} />
                            )}
                          </Card>
                        )}
                        <File
                          path="/api/uploadProductImage"
                          onSubmit={onFileUpload}
                          name="img"
                          id={id}
                          disabled={roleLevel < 9}
                          preUploadCallback={() => setDisabled(true)}
                          postUploadCallback={() => setDisabled(false)}
                        />
                      </Flex>
                      <Box color={theme.danger}>{errors["img"]}</Box>
                    </Flex> */}
                  </Flex>

                  <Flex
                    flexDirection="column"
                    alignItems="flex-start"
                    width={[1, 1, 0.5]}
                  >
                    {values.isNonConfigurable ? (
                      <>
                        <Field
                          disabled={roleLevel < 9}
                          labelText="Quantity In Stock"
                          name="quantityInStock"
                          type="number"
                        />
                        <Field
                          labelText="Fixed Price: "
                          name="defaultPrice"
                          type="number"
                          // disabled={
                          //   disabled || !values?.isNonConfigurable || false
                          // }
                          disabled={roleLevel < 9}
                        />
                        <Field
                          labelText="Related Item: "
                          name="RelatedItem"
                          type="textarea"
                          disabled={roleLevel < 9}
                        />
                      </>
                    ) : (
                      <>
                        <Field
                          disabled={roleLevel < 9}
                          labelText="Header ID: "
                          name="HeaderId"
                        />
                        <Field
                          disabled={roleLevel < 9}
                          labelText="Application Instance: "
                          name="ApplicationInstance"
                        />
                        <Field
                          disabled={roleLevel < 9}
                          labelText="Application Name: "
                          name="ApplicationName"
                        />
                        <Field
                          disabled={roleLevel < 9}
                          labelText="Profile: "
                          name="Profile"
                        />

                        <Field
                          disabled={roleLevel < 9}
                          labelText="Ruleset: "
                          name="Ruleset"
                        />
                        <Field
                          disabled={roleLevel < 9}
                          labelText="Namespace: "
                          name="Namespace"
                        />
                        <Field
                          disabled={roleLevel < 9}
                          labelText="Server: "
                          name="Server"
                        />
                        <Checkbox
                          disabled={roleLevel < 9}
                          labelText="Show this product in Quick Configure."
                          name="isQuickConfig"
                        />
                      </>
                    )}
                  </Flex>
                </Flex>

                <FormButtonCluster
                  // disabled={disabled}
                  onCancel={() => navigate("/Products")}
                  onDelete={onDelete}
                  recordAction={action}
                  hideDelete={roleLevel !== 9 || action === "create"}
                  deleteMessage={`Are you sure you want to delete ${values.Name}?`}
                />
              </Form>
            );
          }}
        </Formik>
      )}
    </PageWrapper>
  );
};

export default ProductForm;
